import * as Sentry from '@sentry/react';
import { Button, Result, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Contains a render crash to the page it happened on.
 *
 * The admin console had no boundary anywhere, so ANY throw during render
 * unmounted the whole React tree and left a blank console — no nav, no way
 * back, nothing on screen saying what happened. That is how a single missing
 * field took the panel down: `detail.audience.length` on an ended Live session
 * threw, and the operator saw a white page rather than "this one session is
 * broken".
 *
 * Live data is the worst case for this. An ops console reads sessions that end,
 * get reaped, or were written by an older backend, so a field the type system
 * promises is present can simply be absent at runtime. Guarding each field is
 * necessary but never complete — this is the floor under it.
 *
 * Deliberately NOT a swallow: the error is reported to Sentry (via
 * `Sentry.ErrorBoundary`, which uses the redacting `beforeSend` in
 * observability/sentry.ts) and shown to the operator with its message. The
 * boundary changes the blast radius, not the visibility.
 *
 * Keyed on the route, so navigating away from a broken page resets it — an
 * operator should never have to reload the console to escape one bad row.
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <Sentry.ErrorBoundary
      key={location.pathname}
      // Taken as one object rather than destructured: pulling `resetError` out
      // trips @typescript-eslint/unbound-method, since it is a method of the
      // boundary instance.
      fallback={(props) => (
        <Result
          status="error"
          title="This page hit an error"
          subTitle="The rest of the console still works. The error has been reported."
          extra={[
            <Button type="primary" key="retry" onClick={() => props.resetError()}>
              Try again
            </Button>,
          ]}
        >
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            <Typography.Text code>
              {props.error instanceof Error
                ? props.error.message
                : String(props.error)}
            </Typography.Text>
          </Typography.Paragraph>
        </Result>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
